import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ResponsiveModal, ResponsiveModalContent } from "@/components/ui/responsive-modal";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { RefreshCw } from "lucide-react";

const AVATAR_STYLES = [
  { name: 'Adventurer', value: 'adventurer' },
  { name: 'Adventurer Neutral', value: 'adventurer-neutral' },
  { name: 'Avataaars', value: 'avataaars' },
  { name: 'Big Ears', value: 'big-ears' },
  { name: 'Bottts', value: 'bottts' },
  { name: 'Croodles', value: 'croodles' },
  { name: 'Micah', value: 'micah' },
  { name: 'Personas', value: 'personas' },
  { name: 'Pixel Art', value: 'pixel-art' },
  { name: 'Thumbs', value: 'thumbs' },
];

interface AvatarSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (avatarUrl: string, source: 'dicebear' | 'google') => void;
  currentAvatarUrl?: string;
  showGoogleOption?: boolean; // Allow hiding Google option for admin
}

export function AvatarSelector({ isOpen, onClose, onSelect, currentAvatarUrl, showGoogleOption = true }: AvatarSelectorProps) {
  const { user } = useAuth();
  const [selectedStyle, setSelectedStyle] = useState('adventurer');
  const [selectedSeed, setSelectedSeed] = useState(user?.id || 'random');
  const onCloseRef = useRef(onClose);

  // Keep the ref updated with the latest onClose callback
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const googleAvatarUrl = showGoogleOption ? user?.user_metadata?.avatar_url : null;

  const generateDiceBearUrl = (style: string, seed: string) => {
    return `https://api.dicebear.com/7.x/${style}/svg?seed=${seed}`;
  };

  const handleRandomize = () => {
    setSelectedSeed(Math.random().toString(36).substring(7));
  };

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;
    const onPopState = (e: PopStateEvent) => {
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPopState);
    window.history.pushState(null, '', window.location.href);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [isOpen]);

  return (
    <ResponsiveModal open={isOpen} setOpen={onClose}>
      <ResponsiveModalContent 
        className="bg-background text-foreground w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-0"
      >
        <div className="space-y-4 p-4 sm:p-6">
          {/* Google Profile Picture Option */}
          {googleAvatarUrl && (
            <div className="space-y-2">
              <div className="flex gap-4 items-center">
                <Avatar className="w-20 h-20 sm:w-24 sm:h-24 border-2 border-primary">
                  <AvatarImage src={googleAvatarUrl} />
                </Avatar>
                <div className="flex flex-col justify-center">
                  <p className="text-sm text-muted-foreground">Use your Google account picture</p>
                  <Button 
                    size="sm" 
                    onClick={() => {
                      onSelect(googleAvatarUrl, 'google');
                      onClose();
                    }}
                    className="mt-2 bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    Select
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* DiceBear Avatar Options */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground text-sm sm:text-base">Avatar Styles</h3>
              <Button variant="outline" size="sm" onClick={handleRandomize} className="text-xs sm:text-sm">
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Randomize
              </Button>
            </div>
            
            {/* Style Selection */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
              {AVATAR_STYLES.map((style) => (
                <button
                  key={style.value}
                  onClick={() => {
                    onSelect(generateDiceBearUrl(style.value, selectedSeed), 'dicebear');
                    onClose();
                  }}
                  className={`p-2 sm:p-3 rounded-lg border-2 transition-all ${
                    selectedStyle === style.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <Avatar className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto">
                    <AvatarImage src={generateDiceBearUrl(style.value, selectedSeed)} />
                  </Avatar>
                  <p className="text-[10px] sm:text-xs text-center mt-1 sm:mt-2 text-foreground">{style.name}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

